// Where did this session actually come from — the iOS app, the Android app, or
// the web?
//
// ⚠️ Issue #355. This used to live twice, copy-pasted into analytics.ts and
// utmAttribution.ts, and BOTH copies had the same bug: they treated the
// presence of the Median JS bridge as proof of iOS.
//
//     if (window.median) return "ios_app_store";   // ← wrong
//
// Median injects `window.median` into BOTH native shells. It is the "am I in
// the native app?" signal, not the "am I on iOS?" signal. So every Android app
// session was recorded as `ios_app_store`, and had been since the June 1, 2026
// cutover (~8 weeks before it was noticed) — silently, because the value is
// plausible and nothing errors.
//
// The distinguishing signal is the user agent. Median's Android shell runs a
// Chrome WebView whose UA carries `Android`; the iOS shell runs WKWebView with
// `iPhone`/`iPad`/`iPod` and never says `Android`. So the native branch reads
// the UA rather than assuming, and the iOS answer is unchanged for every UA it
// used to catch.
//
// There is ONE home for this now, imported by both writers, precisely so the
// next fix can't land in one file and miss the other.

/** The values written to `user_sessions.platform`, `daily_active_users.platform`,
 *  and `signup_attribution.platform`. All are plain text columns — no enum, no
 *  check constraint — so this union is the only thing pinning them down. */
export type Platform = "ios_app_store" | "android" | "web_referral" | "web_direct";

/** Everything the decision depends on, lifted out of globals so it can be
 *  tested without a fake `window`. */
export interface PlatformSignals {
  /** `window.median` exists — the native shell injected its JS bridge. True on
   *  iOS AND Android; this alone tells you nothing about which. */
  hasMedianBridge: boolean;
  /** `navigator.userAgent`. */
  userAgent: string;
  /** `document.referrer` is non-empty. */
  hasReferrer: boolean;
}

/** Median appends its name to the UA in both shells. Used as a SECOND way to
 *  recognise the native app, not as a replacement for the bridge check: the UA
 *  can say "median" before the bridge has been injected (see the same race
 *  documented in useHideKeyboardAccessory.ts), and the bridge can be there on a
 *  shell whose UA was customised. Either one means native. */
const MEDIAN_UA = /median|gonative/i;

/**
 * Does this user agent claim to be the native shell?
 *
 * Exported because `SocialAuthButtons.tsx` needs the SAME answer when it
 * decides whether to route Google through the native Social Login bridge. It
 * used to ask `userAgent.indexOf("median") >= 0` — case-sensitive, and blind to
 * `gonative` — so a shell whose UA said `Median` or `GoNative` would be counted
 * as native here and sent to the webview OAuth redirect there, which Google
 * 403s as `disallowed_useragent`. Issue #1559.
 *
 * ⚠️ The two matchers have never actually disagreed on real traffic: measured
 * Sep 11 2026 over all 288 rows of `auth.sessions`, 114 carry a Median UA and
 * every one of them is lowercase `median`. This is a latent divergence closed
 * for hygiene, NOT the cause of any known failure — don't let the comment grow
 * into a claim that it fixed something.
 */
export const isMedianShellUA = (ua: string): boolean => MEDIAN_UA.test(ua || "");

const ANDROID_UA = /Android/i;

const IOS_UA = /iPhone|iPad|iPod/i;

/**
 * Which app store to offer a MOBILE BROWSER visitor, or null to offer nothing.
 * Issue #1703.
 *
 * Lives here rather than in the banner component for the reason #355 exists: this
 * repo has had two copies of platform detection before, they diverged, and every
 * Android session for eleven weeks was recorded as iOS. `platformDetection.test.ts`
 * fails if a consumer grows its own detector back, so a new UA test belongs in this
 * file next to the others, reusing the same constants.
 *
 * `null` in three distinct cases, and conflating them is the bug to avoid:
 *   - inside the Median shell — she already HAS the app; nudging her to install it
 *     is the most obviously broken thing this feature could do
 *   - desktop — the store link is useless there
 *   - an unreadable UA — degrade to showing nothing, never to guessing
 *
 * ⚠️ Deliberately NOT `detectPlatform()`. That returns `"android"` for the Android
 * APP and Android mobile web alike (a conflation predating #355 and left alone on
 * purpose), so it cannot answer "is this a browser?" — which is the entire question
 * here.
 */
export type StoreTarget = "ios" | "android" | null;

export const storeTargetFor = (ua: string): StoreTarget => {
  const s = typeof ua === "string" ? ua : "";
  if (!s) return null;
  if (isMedianShellUA(s)) return null;
  if (IOS_UA.test(s)) return "ios";
  if (ANDROID_UA.test(s)) return "android";
  return null;
};

const ANDROID_VERSION_UA = /Android (\d{1,3}(?:\.\d{1,3})?)/i;
// iPadOS 13+ desktop-class Safari says "Macintosh" and carries no iOS version;
// that correctly falls through to "unknown" rather than guessing.
const IOS_VERSION_UA = /(?:iPhone|iPad|iPod)[^)]*? OS (\d{1,3})(?:_(\d{1,3}))?/i;

/**
 * OS version for a Sentry tag — `android-14`, `ios-17.0`, or `unknown`. #1705.
 *
 * Deliberately tiny: Android and iOS only, and only digits make it into the
 * value, so the tag stays low-cardinality whatever a UA contains. Not a
 * general UA parser and must not grow into one — no dependency for this.
 */
export const parseOsVersion = (ua: string): string => {
  const s = typeof ua === "string" ? ua : "";
  const android = ANDROID_VERSION_UA.exec(s);
  if (android) return `android-${android[1]}`;
  const ios = IOS_VERSION_UA.exec(s);
  if (ios) return `ios-${ios[1]}.${ios[2] ?? "0"}`;
  return "unknown";
};

/**
 * Pure form of `detectPlatform()`. Exported for tests and for anything that
 * needs to reason about a UA it isn't currently running under.
 *
 * Order matters and is deliberate:
 *   1. Native shell? Then ask the UA which one. Android → `android`, everything
 *      else → `ios_app_store`. The fallback stays iOS on purpose — that is what
 *      every non-Android native UA resolved to before, so iOS detection is not
 *      weakened by this fix, only Android is subtracted from it.
 *   2. Plain Android browser → `android`. (Unchanged: this value has always
 *      covered Android app and Android mobile web alike. Splitting them would
 *      be a new `play_store` value and a schema-wide decision, not this fix.)
 *   3. Otherwise web, split by whether a referrer brought them.
 */
export const resolvePlatform = (signals: PlatformSignals): Platform => {
  const { hasMedianBridge, userAgent, hasReferrer } = signals;
  const ua = userAgent || "";

  const isNativeShell = hasMedianBridge || isMedianShellUA(ua);
  if (isNativeShell) return ANDROID_UA.test(ua) ? "android" : "ios_app_store";

  if (ANDROID_UA.test(ua)) return "android";
  return hasReferrer ? "web_referral" : "web_direct";
};

/**
 * Read the live browser globals and resolve the platform.
 *
 * Never throws: analytics and attribution are both fire-and-forget and must
 * never break the app, so an unreadable environment degrades to `web_direct`
 * rather than propagating.
 */
export const detectPlatform = (): Platform => {
  try {
    if (typeof window === "undefined") return "web_direct";
    return resolvePlatform({
      hasMedianBridge: !!(window as unknown as { median?: unknown }).median,
      userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent || "",
      hasReferrer: typeof document !== "undefined" && !!document.referrer,
    });
  } catch {
    return "web_direct";
  }
};
